// The v3 framework store (zustand) — Ben's Phase 03. Single source of truth for
// the placed modules, the sticky default player, and the undo history.
//
// This is a PORT of main.js's model (see docs/frontend-v3/phase-03-mainjs-seams.md),
// NOT the v2 canvas store — the two describe different apps. Faithful choices from
// main.js:
//   - modules carry px geometry (x/y = style.left/top) + cols/rows
//     (data-base-cols/rows), a `title` type key, `pinned`, a scalar `player`, and
//     the generic `custom` `_`-prefixed-state bag.
//   - undo is EXPLICIT: a caller pushes a checkpoint BEFORE a mutation (main.js's
//     ~15 saveState() call sites), not auto-on-mutate — so the interaction code keeps
//     the exact control it has today (incl. the skip-flag dedupe).
//   - the store keeps the history; the CALLER captures each step (src/canvas/undo.ts).
//     Until the module list here is live (05b) a step is read off the DOM.
//
// Live since 05a (undo history); the sticky default player since 03d (the only copy since
// 05c). The module list is
// live since 05b: React renders one `.placed-module` per entry (src/canvas/Canvas.tsx) —
// which fields of an entry are live yet is spelled out on PlacedModule (types.ts).

import { create } from "zustand";
import type { ProtoStoreState } from "./types.js";

/** Undo depth. Ben's #179 raised it 10 → 50 once changes inside widgets became undo
 *  steps too (a snapshot is a few KB). */
export const UNDO_LIMIT = 50;

let idCounter = 0;
/**
 * A session-only module id. main.js uses the DOM node's own identity as the key;
 * in React we need an explicit stable key, minted here and never persisted.
 */
export function mintModuleId(): string {
  idCounter += 1;
  return `m${Date.now().toString(36)}-${idCounter.toString(36)}`;
}

/**
 * Build a fresh store instance. The app imports the `useProtoStore` singleton
 * below; tests use the factory so each test starts clean (no shared-singleton
 * bleed between cases).
 */
export function createProtoStore() {
  return create<ProtoStoreState>((set, get) => ({
    modules: [],
    selectedPlayer: null,
    selectedIds: [],
    workspaceShiftX: 0,
    workspaceShiftY: 0,
    undoHistory: [],
    historyChanges: 0,
    checkpointsHeld: false,

    addModule: (module, { atStart = false } = {}) => {
      const id = module.id ?? mintModuleId();
      const placed = { ...module, id };
      // The list order is the DOM order (= stacking order), so "at start" is main.js's
      // insertBefore(firstModule) for the mobile prepend.
      set((s) => ({ modules: atStart ? [placed, ...s.modules] : [...s.modules, placed] }));
      return id;
    },

    // A removed module leaves the selection too (main.js: its class went with the element).
    removeModule: (id) => {
      set((s) => ({
        modules: s.modules.filter((m) => m.id !== id),
        selectedIds: s.selectedIds.includes(id) ? s.selectedIds.filter((x) => x !== id) : s.selectedIds,
      }));
    },

    clearModules: () => {
      set({ modules: [], selectedIds: [] });
    },

    // One set for the lot, and nothing at all when no box changed (a cancelled drag).
    commitBoxes: (boxes) => {
      set((s) => {
        const byId = new Map(boxes.map((b) => [b.id, b]));
        let changed = false;
        const modules = s.modules.map((m) => {
          const b = byId.get(m.id);
          if (!b || (b.x === m.x && b.y === m.y && b.cols === m.cols && b.rows === m.rows)) return m;
          changed = true;
          return { ...m, x: b.x, y: b.y, cols: b.cols, rows: b.rows };
        });
        return changed ? { modules } : s;
      });
    },

    // Module order, so "the selection" reads the way main.js's querySelectorAll did (DOM
    // order). An unchanged selection keeps the same array: a box-select drag sets it on
    // every pointer move, and only a real change should re-render.
    setSelectedIds: (ids) => {
      set((s) => {
        const wanted = new Set(ids);
        const next = s.modules.filter((m) => wanted.has(m.id)).map((m) => m.id);
        const same = next.length === s.selectedIds.length && next.every((id, i) => id === s.selectedIds[i]);
        return same ? s : { selectedIds: next }; // returning `s` itself notifies no one
      });
    },

    updateModule: (id, patch) => {
      set((s) => ({
        modules: s.modules.map((m) => (m.id === id ? { ...m, ...patch } : m)),
      }));
    },

    setSelectedPlayer: (id) => {
      set({ selectedPlayer: id });
    },

    setWorkspaceShift: (x, y) => {
      set({ workspaceShiftX: x, workspaceShiftY: y });
    },

    // main.js pushCheckpoint(): push, count it, drop the oldest past the cap. Deep-cloned
    // so a caller can't change a past step through a shared `custom` object.
    pushCheckpoint: (snapshot) => {
      set((s) => {
        const next = [...s.undoHistory, structuredClone(snapshot)];
        return {
          undoHistory: next.length > UNDO_LIMIT ? next.slice(next.length - UNDO_LIMIT) : next,
          historyChanges: s.historyChanges + 1,
        };
      });
    },

    // main.js triggerUndo()'s pop: an empty history returns early WITHOUT counting (so the
    // undo net doesn't see a change that never happened).
    popCheckpoint: () => {
      const { undoHistory, historyChanges } = get();
      const previous = undoHistory[undoHistory.length - 1];
      if (!previous) return undefined;
      set({ undoHistory: undoHistory.slice(0, -1), historyChanges: historyChanges + 1 });
      return previous;
    },

    // main.js's `historyStack.pop(); historyChanges++;` — counted even when empty, as there.
    dropLastCheckpoint: () => {
      set((s) => ({ undoHistory: s.undoHistory.slice(0, -1), historyChanges: s.historyChanges + 1 }));
    },

    setCheckpointsHeld: (held) => {
      set({ checkpointsHeld: held });
    },

    // Wipe framework state (main.js clearWorkspace, minus the DOM/grid side of it).
    reset: () => {
      set({
        modules: [],
        selectedPlayer: null,
        selectedIds: [],
        workspaceShiftX: 0,
        workspaceShiftY: 0,
        undoHistory: [],
        historyChanges: 0,
        checkpointsHeld: false,
      });
    },
  }));
}

/** The app-wide store singleton. */
export const useProtoStore = createProtoStore();
